<template>
  <div>
    <Spinner v-if="loading" />
    <StaticDataTable class="static-table-list" v-show="!loading && !loadingError">
      <template v-slot:header>
        <tr>
          <th width="20%">Price Type</th>
          <th width="40%">Production Stages</th>
          <th width="40%">Production Stations</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="priceGroup in priceGroupList" :key="priceGroup.id">
          <td class="font-weight-bold">
            {{priceGroup.name}}
          </td>
          <td v-if="!editing">
            <template v-for="(stage, stageIndex) of stageListForPriceGroup(priceGroup.id)">
              <span v-if="stageIndex > 0" :key="`stage-sep-${stage.id}`">, </span>
              <LinkProductionStage :key="stage.id" :id="stage.id" :name="stage.name" />
            </template>
            <span v-if="stageListForPriceGroup(priceGroup.id).length == 0" class="font-italic text-muted">Default</span>
          </td>
          <td v-if="!editing">
            <template v-for="(station, stationIndex) of stationListForPriceGroup(priceGroup.id)">
              <span v-if="stationIndex > 0" :key="`station-sep-${station.id}`">, </span>
              <LinkProductionStation :key="station.id" :id="station.id" :name="station.name" />
            </template>
            <span v-if="stationListForPriceGroup(priceGroup.id).length == 0" class="font-italic text-muted">Default</span>
          </td>

          <td v-if="editing">
            <FormMultiSelect
              :value="convertListToIDs(stageListForPriceGroup(priceGroup.id))"
              :options="productionStageLookup"
              @input="updateStageListForPriceGroup(priceGroup.id, $event)"
              placeholder="Select stage overrides" />
          </td>
          <td v-if="editing">
            <FormMultiSelect
              :value="convertListToIDs(stationListForPriceGroup(priceGroup.id))"
              :options="productionStationLookup"
              @input="updateStationListForPriceGroup(priceGroup.id, $event)"
              placeholder="Select station overrides" />
          </td>

        </tr>
      </template>
    </StaticDataTable>
    <span class="text-danger" v-if="loadingError">{{loadingError}}</span>
  </div>
</template>


<script>
import _ from 'lodash'
import formMixin from '@/mixins/form'

export default {
  name: 'ProductionGlassStockLinkEditor',
  model: {
    event: 'update'
  },
  mixins: [formMixin],
  props: {
    editing: Boolean,
    stockId: Number,
    value: Object
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      priceGroupList: [],
      productionStageList: [],
      productionStationList: [],
      stockProductionConfig: _.cloneDeep(this.$props.value || {})
    }
  },
  computed: {
    productionStageLookup() {
      return this.productionStageList.map(stage => {
        return {
          value: stage.id,
          name: stage.name,
          colour: stage.highlightColour
        }
      })
    },
    productionStationLookup() {
      return this.productionStationList.map(station => {
        return {
          value: station.id,
          name: station.name
        }
      })
    }
  },
  watch: {
    value: function() {
      this.$data.stockProductionConfig = _.cloneDeep(this.$props.value)
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.productionService.getProductionStageList(),
        this.$apiService.productionService.getProductionStationList()
      ])
      .then(results => {
        this.priceGroupList = results[0].data
        this.productionStageList = results[1].data
        this.productionStationList = results[2].data
      })
      .catch(err => {
        this.loadingError = 'Failed to load data'
      })
      .finally(() => {
        this.loading = false
      })
    },

    stageListForPriceGroup(stockPriceGroupId) {
      if(!this.stockProductionConfig || !this.stockProductionConfig.productionStageOverrideList)
        return []

      return this.stockProductionConfig.productionStageOverrideList
      .filter(configData => configData.stockPriceGroupId == stockPriceGroupId)
      .map(configData => {
        let stageInfo = this.productionStageList.find(stage => stage.id == configData.productionStageId)
        return {
          id: configData.productionStageId,
          value: configData.productionStageId,
          name: stageInfo ? stageInfo.name : '',
        }
      })
    },
    updateStageListForPriceGroup(stockPriceGroupId, productionStageList) {
      if(!this.stockProductionConfig.productionStageOverrideList)
        this.stockProductionConfig.productionStageOverrideList = []

      // Clear out the price group settings
      this.stockProductionConfig.productionStageOverrideList = this.stockProductionConfig.productionStageOverrideList.filter(override => {
        return override.stockPriceGroupId != stockPriceGroupId
      })

      // Add the stages
      this.stockProductionConfig.productionStageOverrideList = _.concat(this.stockProductionConfig.productionStageOverrideList, productionStageList.map(stageId => {
        return {
          productionStageId: stageId,
          stockPriceGroupId: stockPriceGroupId,
          stockId: this.stockId
        }
      }))

      this.$emit('update', this.stockProductionConfig)
    },

    stationListForPriceGroup(stockPriceGroupId) {
      if(!this.stockProductionConfig || !this.stockProductionConfig.productionStationOverrideList)
        return []

      return this.stockProductionConfig.productionStationOverrideList
      .filter(configData => configData.stockPriceGroupId == stockPriceGroupId)
      .map(configData => {
        let stationInfo = this.productionStationList.find(station => station.id == configData.productionStationId)
        return {
          id: configData.productionStationId,
          value: configData.productionStationId,
          name: stationInfo ? stationInfo.name : ''
        }
      })
    },
    updateStationListForPriceGroup(stockPriceGroupId, productionStationList) {
      if(!this.stockProductionConfig.productionStationOverrideList)
        this.stockProductionConfig.productionStationOverrideList = []

      // Clear out the price group settings
      this.stockProductionConfig.productionStationOverrideList = this.stockProductionConfig.productionStationOverrideList.filter(override => {
        return override.stockPriceGroupId != stockPriceGroupId
      })

      // Add the stations
      this.stockProductionConfig.productionStationOverrideList = _.concat(this.stockProductionConfig.productionStationOverrideList, productionStationList.map(stationId => {
        return {
          productionStationId: stationId,
          stockPriceGroupId: stockPriceGroupId,
          stockId: this.stockId
        }
      }))

      this.$emit('update', this.stockProductionConfig)
    },
  }


}
</script>